import { CalendarDays, ChevronDown, School, TriangleAlert, X } from 'lucide-react'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '../../context/AuthContext'
import { useActiveScope } from '../../context/ScopeContext'
import { ALL_SCOPE } from '../../services/scope'

const FIELD_CLASS =
  'cursor-pointer bg-transparent text-xs font-bold text-slate-800 focus-visible:outline-2 focus-visible:outline-wicend-500 focus-visible:outline-offset-2 dark:text-slate-200'
const OPTION_CLASS = 'bg-white text-slate-800 dark:bg-slate-900 dark:text-slate-200'

/**
 * Unit + academic year, the two axes almost every screen is read against.
 * On desktop: Two adjacent select fields.
 * On mobile: A compact context chip that opens an accessible modal / bottom sheet,
 * ensuring equal access to both unit and academic year without horizontal overflow.
 */
export function ScopeSwitcher() {
  const { user, activePersona } = useAuth()
  const {
    availableUnits,
    academicYears,
    activeUnitId,
    activeAcademicYearId,
    isGlobalUser,
    isViewingPastOrFutureYear,
    setActiveUnitId,
    setActiveAcademicYearId,
  } = useActiveScope()

  const [mobileOpen, setMobileOpen] = useState(false)

  const isExternalOrFamily =
    activePersona === 'PARENT' ||
    user?.role === 'referral' ||
    user?.role === 'REFERRAL' ||
    user?.role === 'referral_partner' ||
    user?.role === 'REFERRAL_PARTNER'

  if (isExternalOrFamily || availableUnits.length === 0) {
    return null
  }

  const activeUnit = availableUnits.find((u) => u.id === activeUnitId)
  const activeUnitName =
    activeUnitId === ALL_SCOPE
      ? 'Semua Unit'
      : activeUnit?.name ?? 'Pilih Unit'

  const activeYear = academicYears.find((y) => y.id === activeAcademicYearId)
  const activeYearName = activeYear?.name ?? ''

  return (
    <div className="flex items-center">
      {/* Mobile Compact Trigger Pill */}
      <div className="flex md:hidden items-center">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-left text-xs font-bold transition max-w-[170px] sm:max-w-[240px] focus-visible:outline-2 focus-visible:outline-wicend-500 focus-visible:outline-offset-2 ${
            isViewingPastOrFutureYear
              ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200'
              : 'border-slate-200 bg-slate-50 text-slate-800 hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200'
          }`}
          aria-label="Pilih unit sekolah dan tahun ajaran aktif"
          data-testid="shell-scope-mobile-trigger-btn"
        >
          <School size={15} className="shrink-0 text-teal-600 dark:text-teal-400" />
          <span className="truncate">
            {activeUnitName}
            {activeYearName ? ` • ${activeYearName}` : ''}
          </span>
          {isViewingPastOrFutureYear ? (
            <TriangleAlert
              size={13}
              className="shrink-0 text-amber-600 dark:text-amber-400"
              aria-label="Bukan tahun berjalan"
            />
          ) : (
            <ChevronDown size={13} className="shrink-0 opacity-60" />
          )}
        </button>
      </div>

      {/* Desktop Scope Selectors */}
      <div className="hidden md:flex items-center gap-2">
        <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 dark:border-slate-800 dark:bg-slate-900">
          <School size={16} className="shrink-0 text-teal-600 dark:text-teal-400" />
          <select
            aria-label="Pilih unit sekolah aktif"
            data-testid="shell-scope-unit-select"
            value={activeUnitId}
            onChange={(event) => setActiveUnitId(event.target.value)}
            className={`${FIELD_CLASS} max-w-[180px] lg:max-w-none truncate`}
          >
            {isGlobalUser && (
              <option value={ALL_SCOPE} className={OPTION_CLASS}>
                Semua Unit (Yayasan)
              </option>
            )}
            {availableUnits.map((unit) => (
              <option key={unit.id} value={unit.id} className={OPTION_CLASS}>
                {unit.name} ({unit.level})
              </option>
            ))}
          </select>
        </div>

        {academicYears.length > 0 && (
          <div
            className={`items-center gap-2 rounded-md border px-3 py-1.5 flex ${
              isViewingPastOrFutureYear
                ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40'
                : 'border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900'
            }`}
            title={
              isViewingPastOrFutureYear
                ? 'Anda sedang melihat tahun ajaran yang bukan tahun berjalan.'
                : undefined
            }
          >
            {isViewingPastOrFutureYear ? (
              <TriangleAlert
                size={16}
                className="shrink-0 text-amber-600 dark:text-amber-400"
                aria-hidden
              />
            ) : (
              <CalendarDays
                size={16}
                className="shrink-0 text-teal-600 dark:text-teal-400"
                aria-hidden
              />
            )}
            <select
              aria-label="Pilih tahun ajaran aktif"
              data-testid="shell-scope-academic-year-select"
              value={activeAcademicYearId}
              onChange={(event) => setActiveAcademicYearId(event.target.value)}
              className={`${FIELD_CLASS} ${
                isViewingPastOrFutureYear ? 'text-amber-800 dark:text-amber-200' : ''
              }`}
            >
              {academicYears.map((year) => (
                <option key={year.id} value={year.id} className={OPTION_CLASS}>
                  {year.name}
                  {year.isActive ? ' (berjalan)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Mobile Modal / Bottom Sheet */}
      {mobileOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-slate-950/60 p-0 backdrop-blur-sm sm:p-4 animate-in fade-in duration-150"
            onClick={() => setMobileOpen(false)}
            data-testid="shell-scope-mobile-modal"
          >
            <div
              className="w-full max-w-lg rounded-t-2xl sm:rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl dark:border-slate-800 dark:bg-slate-900"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2.5">
                  <div className="flex size-9 items-center justify-center rounded-lg bg-teal-50 text-teal-700 dark:bg-teal-950 dark:text-teal-300">
                    <School size={18} />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                      Konteks Kerja Aktif
                    </h3>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      Pilih unit sekolah dan tahun ajaran
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                  aria-label="Tutup"
                  data-testid="shell-scope-mobile-modal-close-btn"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="mt-4 space-y-4">
                <div>
                  <label className="mb-1.5 block text-xs font-bold text-slate-700 dark:text-slate-300">
                    Unit Sekolah
                  </label>
                  <div className="relative">
                    <select
                      aria-label="Pilih unit sekolah aktif di HP"
                      data-testid="shell-scope-mobile-unit-select"
                      value={activeUnitId}
                      onChange={(e) => setActiveUnitId(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-xs font-semibold text-slate-900 focus-visible:outline-2 focus-visible:outline-wicend-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
                    >
                      {isGlobalUser && (
                        <option value={ALL_SCOPE}>Semua Unit (Yayasan)</option>
                      )}
                      {availableUnits.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name} ({unit.level})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {academicYears.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                        Tahun Ajaran
                      </label>
                      {isViewingPastOrFutureYear && (
                        <span className="flex items-center gap-1 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
                          <TriangleAlert size={12} /> Bukan tahun berjalan
                        </span>
                      )}
                    </div>
                    <div className="relative">
                      <select
                        aria-label="Pilih tahun ajaran aktif di HP"
                        data-testid="shell-scope-mobile-academic-year-select"
                        value={activeAcademicYearId}
                        onChange={(e) => setActiveAcademicYearId(e.target.value)}
                        className={`w-full rounded-xl border px-3.5 py-2.5 text-xs font-semibold focus-visible:outline-2 focus-visible:outline-wicend-500 dark:bg-slate-950 ${
                          isViewingPastOrFutureYear
                            ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:text-amber-200'
                            : 'border-slate-200 bg-slate-50 text-slate-900 dark:border-slate-800 dark:text-slate-100'
                        }`}
                      >
                        {academicYears.map((year) => (
                          <option key={year.id} value={year.id}>
                            {year.name}
                            {year.isActive ? ' (berjalan)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => setMobileOpen(false)}
                    className="w-full rounded-xl bg-wicend-700 py-3 text-xs font-bold text-white shadow-sm transition hover:bg-wicend-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-wicend-500"
                    data-testid="shell-scope-mobile-modal-apply-btn"
                  >
                    Selesai
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}
